import { useEffect, useState, useRef } from 'react';
import { Toaster, toast } from 'sonner';
import { usePlatformStore } from './stores/platformStore';
import { useConversationStore } from './stores/conversationStore';
import { useUserStore } from './stores/userStore';
import Sidebar from './components/Sidebar';
import { ErrorBoundary } from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import { Spinner } from './components/Skeleton';
import { AuthPrompt } from './components/AuthPrompt';
import { UserStatusBanner } from './components/UserStatusBanner';
import { apiClient } from './lib/api';
import type { PluginSession } from './lib/types';

function App() {
  const { platform, adapter, isInitialized, error, initializePlatform } = usePlatformStore();
  const { currentConversation } = useConversationStore();
  // Use selective store access to avoid unnecessary rerenders
  const setUserStatus = useUserStore((state) => state.setUserStatus);
  const userStatus = useUserStore((state) => state.userStatus);
  const requiresAuth = useUserStore((state) => state.requiresAuth);
  const authUrl = useUserStore((state) => state.authUrl);
  const [session, setSession] = useState<PluginSession | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);

  // Debug: Count renders
  const renderCount = useRef(0);
  renderCount.current++;
  console.log(`[App] Render #${renderCount.current}`, {
    isInitialized,
    platform,
    error,
    conversationId: currentConversation?.id,
  });

  // Prevent re-initialization
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Guard against multiple initializations
    if (hasInitialized.current) {
      console.log('[App] Already initialized, skipping duplicate init');
      return;
    }

    console.log('[App] Initialization useEffect triggered');
    hasInitialized.current = true;

    // Initialize platform adapter and fetch session
    const init = async () => {
      console.log('[App] Starting initialization...');

      // First, extract auth_secret from URL and set it (BEFORE making API calls!)
      const urlParams = new URLSearchParams(window.location.search);
      const authSecret = urlParams.get('auth_secret');

      if (authSecret) {
        console.log('[App] Found auth_secret in URL, setting token');
        apiClient.setToken(authSecret);
      } else {
        console.warn('[App] No auth_secret found in URL - authentication may fail');
      }

      // Then, initialize platform (Front SDK, etc.)
      try {
        await initializePlatform();
        console.log('[App] Platform initialized');
      } catch (err) {
        console.error('[App] Platform initialization failed:', err);
        // Continue anyway - error will be shown
      }

      // Don't fetch session here - wait for teammate info from Front first
      // Session will be fetched in onTeammateChange callback
      console.log('[App] Platform initialized, waiting for teammate info...');
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Set up teammate tracking after platform adapter is initialized
  const hasSetupTeammate = useRef(false);
  const hasFetchedSession = useRef(false);

  // Helper function to fetch session
  const fetchSession = async (teammate?: any) => {
    if (hasFetchedSession.current) {
      console.log('[App] Session already fetched, skipping');
      return;
    }

    try {
      console.log('[App] Fetching session...', teammate ? 'with teammate info' : 'without teammate info');
      const sessionData = await apiClient.getSession();
      setSession(sessionData);
      hasFetchedSession.current = true;

      // Set account ID in API client for future requests
      if (sessionData.account_id) {
        apiClient.setAccountId(sessionData.account_id);
      }

      // Update user status from session
      if (sessionData.user_status) {
        setUserStatus(sessionData.user_status, sessionData.needs_auth || false, sessionData.auth_url);
      }

      // Check if platform is connected
      if (!sessionData.connected) {
        setSessionError(sessionData.message || sessionData.error || 'Platform not connected');

        // Show warning toast
        toast.warning('Front not connected', {
          description: 'Please connect Front in Crewflow Settings to use all features.',
          duration: 10000,
        });
      }

      console.log('[App] Session fetched successfully:', sessionData);
    } catch (err: any) {
      console.error('[App] Failed to fetch session:', err);

      // Check if error is 401 (unauthorized - user needs to authenticate)
      if (err.statusCode === 401) {
        console.log('[App] 401 Unauthorized - user needs authentication:', err.response);

        // Always set requiresAuth on 401, even if response is missing/malformed
        const needsAuth = err.response?.needs_auth !== false; // Default to true if missing
        const userStatusValue = err.response?.user_status || 'inactive';
        const authUrlValue = err.response?.auth_url || undefined;

        // Set partial session data if available
        if (err.response) {
          setSession(err.response);
        }

        // Set user status to trigger auth prompt
        setUserStatus(userStatusValue, needsAuth, authUrlValue);
      } else {
        setSessionError(err.message || 'Failed to load session');
      }
    }
  };

  useEffect(() => {
    if (!adapter || !isInitialized || hasSetupTeammate.current) {
      return;
    }

    // Check if adapter has onTeammateChange method (Front adapter)
    if ('onTeammateChange' in adapter && typeof adapter.onTeammateChange === 'function') {
      console.log('[App] Setting up teammate tracking');
      hasSetupTeammate.current = true;

      adapter.onTeammateChange(async (teammate: any) => {
        console.log('[App] Teammate changed:', teammate);

        // Update user store
        useUserStore.getState().setPlatformUser(teammate.id, teammate.email, teammate.name);

        // Update API client with teammate headers
        apiClient.setPlatformUser(teammate.id, teammate.email, teammate.name);

        // Fetch session now that we have teammate info
        await fetchSession(teammate);
      });

      // Fallback: If Front doesn't trigger the callback within 5 seconds, check if we should fetch
      const fallbackTimeout = setTimeout(() => {
        if (!hasFetchedSession.current) {
          // Only fetch if we have platform user info (otherwise we'll get 401)
          const platformUser = apiClient.getPlatformUser();
          if (platformUser.userId && platformUser.email) {
            console.warn('[App] Front teammate callback not triggered within 5s, but have user info - fetching session');
            fetchSession();
          } else {
            console.warn('[App] Front teammate callback not triggered within 5s and no user info - waiting longer');
          }
        }
      }, 5000);

      return () => clearTimeout(fallbackTimeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, isInitialized]);

  // Add timeout for loading state (10 seconds)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isInitialized) {
        console.error('Loading timeout - still waiting for:', {
          isInitialized,
          platform,
          error,
        });
        setLoadingTimedOut(true);
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [isInitialized, platform, error]);

  // Show timeout error state
  if (loadingTimedOut) {
    return (
      <div className="flex items-center justify-center h-screen p-4">
        <div className="text-center max-w-md">
          <h2 className="text-lg font-semibold mb-2 text-amber-600">
            Loading Timeout
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Initialization is taking longer than expected.
          </p>
          <div className="bg-muted/50 rounded-lg p-4 mb-4 text-left">
            <div className="text-xs font-mono space-y-1">
              <div>Platform initialized: {isInitialized ? '✓' : '✗'}</div>
              <div>Platform: {platform || 'none'}</div>
              {error && <div className="text-red-600">Error: {error}</div>}
            </div>
          </div>
          <div className="space-y-2">
            <button
              onClick={() => {
                console.log('[App] Manual retry clicked, reloading...');
                window.location.reload();
              }}
              className="w-full px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Retry
            </button>
            <p className="text-xs text-muted-foreground">
              Running locally?{' '}
              <a
                href="https://github.com/crewflow/crewflow/blob/main/docs/DEVELOPMENT.md#mock-data"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                Enable mock data
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen p-4">
        <div className="text-center max-w-md">
          <h2 className="text-lg font-semibold mb-2 text-red-600">
            Failed to Initialize
          </h2>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          {error.includes('Front SDK not available') && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4 text-left">
              <p className="text-xs text-blue-800 dark:text-blue-200">
                <strong>Running locally?</strong> Mock data is automatically enabled in development mode.
                The plugin should work without Front SDK. If you see this error, try:
              </p>
              <ol className="text-xs text-blue-700 dark:text-blue-300 mt-2 ml-4 space-y-1 list-decimal">
                <li>Check browser console for detailed errors</li>
                <li>Verify VITE_API_URL is correct in .env</li>
                <li>Ensure API server is running</li>
              </ol>
            </div>
          )}
          <button
            onClick={() => {
              console.log('[App] Manual retry from error state clicked, reloading...');
              window.location.reload();
            }}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show loading state (only if platform not initialized yet)
  if (!isInitialized || !platform) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Initializing Crewflow...</h2>
          <p className="text-sm text-muted-foreground">Connecting to platform...</p>
        </div>
      </div>
    );
  }

  // If platform is initialized but no session yet, show waiting message
  if (!session && !requiresAuth) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Loading...</h2>
          <p className="text-sm text-muted-foreground">Waiting for Front to load user info...</p>
        </div>
      </div>
    );
  }

  // Show auth prompt if user needs to authenticate
  if (requiresAuth) {
    return (
      <AuthPrompt
        authUrl={authUrl || undefined}
        message={session?.message || undefined}
        onAuthComplete={() => {
          // Reload plugin to fetch new session
          window.location.reload();
        }}
      />
    );
  }

  // Show connection error state (but don't block the UI - show degraded mode)
  if (sessionError && session && !session.connected) {
    return (
      <ErrorBoundary>
        <OfflineBanner />
        <Toaster
          position="top-right"
          expand={false}
          richColors
          closeButton
          duration={5000}
        />
        <div className="h-screen overflow-hidden bg-background flex flex-col">
          {/* Connection error banner */}
          <div className="flex-shrink-0 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 text-amber-600 dark:text-amber-400">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  Front Not Connected
                </h3>
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                  {sessionError}
                </p>
                <button
                  onClick={() => window.open('https://app.crewflow.com/settings/integrations', '_blank')}
                  className="mt-2 text-sm font-medium text-amber-800 dark:text-amber-200 hover:text-amber-900 dark:hover:text-amber-100 underline"
                >
                  Open Settings →
                </button>
              </div>
            </div>
          </div>

          {/* Show limited UI - sentiment/questions still work */}
          <div className="flex-1 min-h-0">
            <Sidebar conversation={currentConversation} session={session} />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <OfflineBanner />
      <Toaster
        position="top-right"
        expand={false}
        richColors
        closeButton
        duration={5000}
      />
      <div className="h-screen overflow-hidden bg-background flex flex-col">
        {/* User status banner (if not active) */}
        {userStatus && userStatus !== 'active' && (
          <div className="flex-shrink-0">
            <UserStatusBanner status={userStatus} message={session?.message} />
          </div>
        )}

        {/* Main content */}
        <div className="flex-1 min-h-0">
          <Sidebar
            conversation={currentConversation}
            session={session}
            isUserActive={userStatus === 'active' || !userStatus}
          />
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default App;
